import clientApi from 'api/clientApi'

import {
  AddRuleConfigSaveRequest,
  ClientDetails,
  ClientListRequest,
  ClientListResponse,
  RuleConfigListRequest,
  RuleConfigListResponse
} from './types'
import { EmailConfigForm } from './clientEmail/utils'

const ClientApiSlice = clientApi.injectEndpoints({
  endpoints: (build) => ({
    getClient: build.query<ClientListResponse, ClientListRequest>({
      query: ({ filters, page }) => {
        const params: { [key: string]: any } = {
          size: page?.size,
          page: page?.number ? page.number - 1 : 0
        }

        if (filters && filters.length > 0) {
          filters.forEach(({ fieldName, fieldValue }) => {
            if (
              fieldValue === null ||
              fieldValue === undefined ||
              fieldValue === '' ||
              fieldValue === 'all'
            ) {
              delete params[fieldName]
            } else {
              params[fieldName] = fieldValue
            }
          })
        }

        return {
          url: `/document-ai/v1/clients`,
          method: 'GET',
          params
        }
      },
      providesTags: ['clientApi']
    }),
    getClientTask: build.query<ClientListResponse, ClientListRequest>({
      query: ({ filters, page, id, searchKey }) => {
        const params: { [key: string]: any } = {
          size: page?.size,
          page: page?.number ? page.number - 1 : 0,
          sort: 'emailsReceivedAt,desc'
        }

        // Add search parameter if provided (backend expects 'search' param)
        if (searchKey && searchKey.trim() !== '') {
          params.search = searchKey.trim()
        }

        if (filters && filters.length > 0) {
          filters.forEach(({ fieldName, fieldValue }) => {
            if (
              fieldValue === null ||
              fieldValue === undefined ||
              fieldValue === '' ||
              fieldValue === 'all'
            ) {
              delete params[fieldName]
            } else {
              params[fieldName] = fieldValue
            }
          })
        }

        return {
          url: `/document-ai/v1/email-task/${id}`,
          method: 'GET',
          params
        }
      },
      providesTags: ['clientApi']
    }),

    createDownloadReport: build.mutation<Blob, { id: string }>({
      query: ({ id }) => ({
        url: `/document-ai/v1/download-document/${id}`,
        method: 'GET',
        headers: {
          Accept:
            'application/pdf, application/csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/octet-stream'
        },
        responseHandler: (response: any) => response.blob(),
        responseType: 'blob',
        includeAuth: false
      }),
      invalidatesTags: ['clientApi']
    }),

    CreateRedo: build.mutation<ClientListResponse, ClientListRequest>({
      query: ({ id }) => {
        return {
          url: `/document-ai/v1/process-document/${id}`,
          method: 'POST'
        }
      },
      invalidatesTags: ['clientApi']
    }),
    getFieldMappingList: build.query<ClientListResponse, ClientListRequest>({
      query: () => ({
        url: '/document-ai/v1/field-mappings',
        method: 'GET'
      }),
      providesTags: ['clientApi']
    }),
    addRuleConfig: build.mutation<AddRuleConfigSaveRequest, any>({
      query: ({ addRuleConfigDTO }) => {
        return {
          url: '/document-ai/v1/rule-config',
          data: addRuleConfigDTO,
          method: 'POST'
        }
      },
      invalidatesTags: ['clientApi']
    }),
    getRuleConfigList: build.query<
      RuleConfigListResponse,
      RuleConfigListRequest
    >({
      query: () => ({
        url: '/document-ai/v1/rule-config',
        method: 'GET'
      }),
      providesTags: ['clientApi']
    }),
    getTaskLog: build.query<any, { id: string }>({
      query: ({ id }) => {
        return {
          url: `/document-ai/v1/process-logs/${id}`,
          method: 'GET'
        }
      },
      providesTags: ['clientApi']
    }),
    createClient: build.mutation<any, ClientDetails>({
      query: (client) => {
        return {
          url: '/document-ai/v1/clients',
          data: client,
          method: 'POST'
        }
      },
      invalidatesTags: ['clientApi']
    }),
    updateClient: build.mutation<
      any,
      { id: string | number, client: ClientDetails }
    >({
      query: ({ id, client }) => {
        return {
          url: `/document-ai/v1/clients/${id}`,
          data: client,
          method: 'PUT'
        }
      },
      invalidatesTags: ['clientApi']
    }),
    getGrantType: build.query<string[], { type?: string }>({
      query: () => {
        const url = '/document-ai/v1/enums/grant-types'
        return {
          url,
          method: 'GET'
        }
      },
      providesTags: ['clientApi']
    }),
    getIntegrationType: build.query<string[], { type?: string }>({
      query: () => {
        const url = '/document-ai/v1/enums/integration-types'
        return {
          url,
          method: 'GET'
        }
      },
      providesTags: ['clientApi']
    }),
    getClientEmail: build.query<ClientListResponse, ClientListRequest>({
      query: ({ id }) => {
        return {
          url: `/document-ai/v1/email-details/${id}`,
          method: 'GET'
        }
      },
      providesTags: ['clientApi']
    }),
    getObjectTypes: build.query<string[], { type?: string }>({
      query: () => {
        const url = '/document-ai/v1/enums/object-types'
        return {
          url,
          method: 'GET'
        }
      },
      providesTags: ['clientApi']
    }),
    createEmail: build.mutation<any, EmailConfigForm>({
      query: (emailData) => {
        return {
          url: '/document-ai/v1/email-details',
          data: emailData,
          method: 'POST'
        }
      },
      invalidatesTags: ['clientApi']
    }),
    updateEmail: build.mutation<
      any,
      { id: string | number, emailData: EmailConfigForm }
    >({
      query: ({ id, emailData }) => {
        return {
          url: `/document-ai/v1/email-details/${id}`,
          data: emailData,
          method: 'PUT'
        }
      },
      invalidatesTags: ['clientApi']
    }),
    getSystemMappingId: build.query<any, { sourceClientId: number }>({
      query: ({ sourceClientId }) => ({
        url: '/document-ai/v1/integrations',
        method: 'GET',
        params: {
          id: sourceClientId
        }
      }),
      providesTags: ['clientApi']
    })
  })
})
export const {
  useGetClientQuery,
  useGetTaskLogQuery,
  useGetClientTaskQuery,
  useCreateDownloadReportMutation,
  useCreateRedoMutation,
  useGetFieldMappingListQuery,
  useAddRuleConfigMutation,
  useGetRuleConfigListQuery,
  useCreateClientMutation,
  useUpdateClientMutation,
  useGetGrantTypeQuery,
  useGetIntegrationTypeQuery,
  useGetClientEmailQuery,
  useGetObjectTypesQuery,
  useCreateEmailMutation,
  useUpdateEmailMutation,
  useGetSystemMappingIdQuery
} = ClientApiSlice
